<template>
  <div class="view-page">
    <div class="ticket-detail">
      <section class="ticket-detail-header">
        <div class="columns is-variable is-5">
          <div class="column is-4 ticket-detail-header__identity">
            <div class="ticket-icon">
              <i class="fas fa-ticket-alt"></i>
            </div>
            <div class="ticket-detail-header__body">
              <div class="title-row">
                <span class="title">Ticket</span>
                <span class="status-pill">
                  <i class="fas fa-circle" :style="{ color: statusDotColor }"></i>
                  {{ ticket.statusName }}
                </span>
              </div>
              <span class="subject-name">{{ ticket.subject }}</span>
              <router-link :to="{ name: 'profile-tickets' }" class="back-link">
                <i class="fas fa-angle-left"></i>
                Regresar a tickets
              </router-link>
            </div>
          </div>

          <div class="column is-8 ticket-detail-header__stats">
            <div class="table-container">
              <table class="table stats-table is-fullwidth">
                <tbody>
                  <tr>
                    <td class="label-cell">Folio</td>
                    <td class="value-cell" colspan="3">
                      <span class="ticket-detail-header__folio">{{ ticket.folio || `TCK-${ticket.id}` }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="label-cell">Módulo</td>
                    <td class="value-cell">{{ ticket.moduleName }}</td>
                    <td class="label-cell label-cell--narrow">Fecha de creación</td>
                    <td class="value-cell">{{ formatDate(ticket.createdAt, 'DD-MMMM-YYYY', systemConfiguration.lang) }}</td>
                  </tr>
                  <tr>
                    <td class="label-cell">Tipo</td>
                    <td class="value-cell" :colspan="ticket.reason ? 1 : 3">{{ ticket.type && ticket.type.name }}</td>
                    <template v-if="ticket.reason">
                      <td class="label-cell label-cell--narrow">Motivo</td>
                      <td class="value-cell">{{ ticket.reason.name }}</td>
                    </template>
                  </tr>
                  <tr>
                    <td class="label-cell">Prioridad</td>
                    <td class="value-cell" colspan="3">
                      <span class="tag ticket-tag" :class="priorityClass(ticket.priorityCode)">
                        {{ ticket.priorityName }}
                      </span>
                      <i
                        class="far fa-edit ticket-detail-header__edit-icon"
                        title="Editar ticket"
                        @click="openEditModal"
                      ></i>
                    </td>
                  </tr>
                  <tr v-if="ticket.caseUrl">
                    <td class="label-cell">URL del caso</td>
                    <td class="value-cell" colspan="3">
                      <a
                        :href="ticket.caseUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="ticket-detail-header__case-link"
                      >
                        Ver caso <i class="fas fa-external-link-alt"></i>
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="ticket-detail-header__actions" v-if="ticket.statusCode !== 'cerrado'">
              <button
                class="button ticket-close-btn"
                type="button"
                @click="askCloseTicket"
              >
                Cerrar ticket
              </button>
            </div>
          </div>
        </div>

        <p v-if="ticket.description" class="ticket-detail-header__description">{{ ticket.description }}</p>
      </section>

      <div class="columns is-variable is-5">
        <div class="column is-8">
          <section class="ticket-thread-card">
            <div class="ticket-thread-card__header">
              <h2 class="ticket-thread-card__title">Conversación</h2>
              <span class="ticket-thread-card__count">{{ mensajes.length }} mensaje(s)</span>
            </div>

            <div class="ticket-thread" v-if="mensajes.length">
              <div
                v-for="message in mensajes"
                :key="message.id"
                class="ticket-message"
                :class="message.authorType === 'cliente' ? '-is-client' : '-is-support'"
              >
                <div class="ticket-message__avatar">{{ getInitials(message.author) }}</div>
                <div class="ticket-message__content">
                  <div class="ticket-message__meta">
                    <span class="ticket-message__author">{{ message.author }}</span>
                    <span class="tag is-light ticket-message__role" :class="message.authorType === 'soporte' ? 'is-info' : ''">
                      {{ roleLabel(message.authorType) }}
                    </span>
                    <span class="ticket-message__date">{{ formatDate(message.createdAt, 'DD-MMMM-YYYY HH:mm', systemConfiguration.lang) }}</span>
                  </div>
                  <div class="ticket-message__bubble">
                    <p class="ticket-message__text">{{ message.text }}</p>
                  </div>
                </div>
              </div>
            </div>
            <p class="ticket-thread__empty" v-else>
              Aún no hay mensajes en este ticket.
            </p>

            <div class="ticket-reply">
              <label class="ticket-reply__label" for="ticket-reply-textarea">Responder</label>
              <div class="control">
                <textarea
                  id="ticket-reply-textarea"
                  class="textarea ticket-reply__textarea"
                  rows="3"
                  maxlength="2000"
                  placeholder="Escribe tu respuesta para el equipo de soporte..."
                  v-model="nuevaRespuesta"
                ></textarea>
              </div>
              <div class="ticket-reply__footer">
                <span class="ticket-reply__counter">{{ nuevaRespuesta.length }}/2000</span>
                <button
                  class="button is-intelligential ticket-reply__submit"
                  type="button"
                  :disabled="!nuevaRespuesta.trim() || sendingReply"
                  :class="{ 'is-loading': sendingReply }"
                  @click="agregarRespuesta"
                >
                  <span class="icon"><i class="fas fa-paper-plane"></i></span>
                  <span>Enviar respuesta</span>
                </button>
              </div>
            </div>
          </section>
        </div>

        <div class="column is-4 ticket-detail-evidence-column">
          <section class="ticket-evidence-card">
            <div class="ticket-evidence-card__header">
              <h2 class="ticket-evidence-card__title">Evidencia</h2>
              <button
                type="button"
                class="button is-small is-light ticket-evidence-card__upload-btn"
                :disabled="uploadingEvidence"
                :class="{ 'is-loading': uploadingEvidence }"
                @click="triggerEvidenceUpload"
              >
                <span class="icon is-small"><i class="fas fa-paperclip"></i></span>
                <span>{{ uploadingEvidence ? 'Subiendo...' : 'Adjuntar evidencia' }}</span>
              </button>
              <input
                type="file"
                ref="evidenceFileInput"
                accept="image/*,video/*,.pdf"
                style="display:none"
                @change="onEvidenceFileSelected"
              >
            </div>

            <div class="ticket-evidence-grid" v-if="evidenceList.length">
              <a
                v-for="item in evidenceList"
                :key="item.id"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
                class="evidence-item"
                :class="`evidence-item--${item.type}`"
              >
                <div class="evidence-item__preview">
                  <img
                    v-if="item.type === 'image'"
                    :src="item.url"
                    :alt="item.name"
                    class="evidence-item__image"
                  />
                  <div v-else-if="item.type === 'video'" class="evidence-item__video-thumb">
                    <i class="fas fa-play-circle evidence-item__play-icon"></i>
                  </div>
                  <div v-else class="evidence-item__doc-thumb">
                    <i class="fas" :class="fileExtension(item.name) === 'pdf' ? 'fa-file-pdf' : 'fa-file-alt'"></i>
                  </div>
                </div>
                <span class="evidence-item__name">{{ item.name }}</span>
              </a>
            </div>
            <p class="ticket-thread__empty" v-else>
              Aún no hay evidencia adjunta a este ticket.
            </p>
          </section>
        </div>
      </div>
    </div>

    <ModalComponent
      class="ticket-close-confirm-modal"
      :active="isConfirmCloseModalOpen"
      titleModal="Cerrar ticket"
      optionalText="¿Estás seguro de que quieres cerrar este ticket?"
      :isConfirm="true"
      textSave="Cerrar Ticket"
      :loader="isClosingTicket"
      @accept="confirmCloseTicket"
      @cancel="cancelCloseTicket"
    />

    <ModalComponent
      :active="isEditModalOpen"
      titleModal="Editar ticket"
      optionalText="Actualiza el módulo, la prioridad o el estatus de este ticket."
      @hdlOnClick="saveEditModal"
      @cancel="closeEditModal"
      :loading="savingChanges"
      :loader="savingChanges"
      :isOverflow="true"
      textSave="Guardar cambios"
    >
      <div class="columns is-multiline">
        <div class="column is-12">
          <div class="field">
            <label class="label" for="edit-ticket-module">Módulo afectado</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select id="edit-ticket-module" v-model="editModuleCode">
                  <option v-for="option in moduleOptions" :key="option.code" :value="option.code">{{ option.label }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-12">
          <div class="field">
            <label class="label" for="edit-ticket-priority">Prioridad</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select id="edit-ticket-priority" v-model="editPriorityCode">
                  <option v-for="option in priorityOptions" :key="option.code" :value="option.code">{{ option.label }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div class="column is-12">
          <div class="field">
            <label class="label" for="edit-ticket-status">Estatus</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select id="edit-ticket-status" v-model="editStatusCode">
                  <option v-for="option in statusOptions" :key="option.code" :value="option.code">{{ option.label }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalComponent>
  </div>
</template>

<script>
import mixins from '@/mixins/formats'
import { mapActions, mapState } from 'vuex'
import { MODULE_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS } from '@/constants/tickets'

export default {
  name: 'TicketDetail',
  mixins: [mixins],
  components: {
    ModalComponent: () => import('@/components/ui/ModalComponent.vue')
  },
  data () {
    return {
      ticket: {},
      mensajes: [],
      evidenceList: [],
      nuevaRespuesta: '',
      isLoading: true,
      sendingReply: false,
      uploadingEvidence: false,
      moduleOptions: MODULE_OPTIONS,
      priorityOptions: PRIORITY_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      editModuleCode: null,
      editPriorityCode: null,
      editStatusCode: null,
      savingChanges: false,
      isEditModalOpen: false,
      isConfirmCloseModalOpen: false,
      isClosingTicket: false
    }
  },
  computed: {
    ...mapState({
      systemConfiguration: state => state.configuration
    }),
    statusDotColor () {
      const map = {
        nuevo: '#0074cc',
        en_proceso: '#b26a00',
        on_hold: '#9aa5b4',
        escalado: '#d32f2f',
        dependencia_cliente: '#b26a00',
        validacion_cliente: '#0074cc',
        resuelto: '#1f9d63',
        cerrado: '#9aa5b4'
      }
      return map[this.ticket.statusCode] || '#9aa5b4'
    }
  },
  async mounted () {
    await this.loadTicket()
  },
  methods: {
    ...mapActions(['getTicket', 'getTicketComments', 'createTicketComment', 'updateTicket', 'closeTicket', 'getTicketEvidence', 'createTicketEvidence']),
    async loadTicket () {
      this.isLoading = true
      const id = Number(this.$route.params.id)
      const [ticket, comments, evidence] = await Promise.all([
        this.getTicket(id),
        this.getTicketComments(id),
        this.getTicketEvidence({ ticketId: id })
      ])
      this.ticket = ticket || {}
      this.mensajes = comments || []
      this.evidenceList = evidence || []
      this.isLoading = false
    },
    openEditModal () {
      this.editModuleCode = this.ticket.moduleCode
      this.editPriorityCode = this.ticket.priorityCode
      this.editStatusCode = this.ticket.statusCode
      this.isEditModalOpen = true
    },
    closeEditModal () {
      if (this.savingChanges) {
        return
      }
      this.isEditModalOpen = false
    },
    async saveEditModal () {
      if (this.savingChanges) {
        return
      }
      const input = { ticketId: Number(this.$route.params.id) }
      let hasChanges = false
      if (this.editModuleCode !== this.ticket.moduleCode) {
        input.moduleCode = this.editModuleCode
        hasChanges = true
      }
      if (this.editPriorityCode !== this.ticket.priorityCode) {
        input.priorityCode = this.editPriorityCode
        hasChanges = true
      }
      if (this.editStatusCode !== this.ticket.statusCode) {
        input.statusCode = this.editStatusCode
        hasChanges = true
      }
      if (!hasChanges) {
        this.isEditModalOpen = false
        return
      }
      this.savingChanges = true
      const updatedTicket = await this.updateTicket(input)
      this.savingChanges = false
      if (updatedTicket) {
        this.ticket = updatedTicket
        this.isEditModalOpen = false
      }
    },
    askCloseTicket () {
      this.isConfirmCloseModalOpen = true
    },
    cancelCloseTicket () {
      this.isConfirmCloseModalOpen = false
    },
    async confirmCloseTicket () {
      this.isClosingTicket = true
      const updatedTicket = await this.closeTicket({ ticketId: Number(this.$route.params.id) })
      this.isClosingTicket = false
      if (updatedTicket) {
        this.ticket.statusCode = updatedTicket.statusCode
        this.ticket.statusName = updatedTicket.statusName
        this.isConfirmCloseModalOpen = false
      }
    },
    priorityClass (code) {
      const map = {
        baja: 'is-light',
        media: 'is-info is-light',
        alta: 'is-warning is-light',
        urgente: 'is-danger is-light'
      }
      return map[code] || 'is-light'
    },
    roleLabel (authorType) {
      return authorType === 'soporte' ? 'Soporte' : 'Tú'
    },
    getInitials (name) {
      if (!name) {
        return '?'
      }
      const parts = name.trim().split(/\s+/)
      const first = parts[0] ? parts[0][0] : ''
      const second = parts.length > 1 ? parts[1][0] : ''
      return `${first}${second}`.toUpperCase()
    },
    async agregarRespuesta () {
      const texto = this.nuevaRespuesta.trim()
      if (!texto || this.sendingReply) {
        return
      }
      this.sendingReply = true
      const comment = await this.createTicketComment({
        ticketId: Number(this.$route.params.id),
        text: texto
      })
      this.sendingReply = false
      if (comment) {
        this.mensajes.push(comment)
        this.nuevaRespuesta = ''
      }
    },
    fileExtension (name) {
      if (!name) {
        return ''
      }
      return (name.split('.').pop() || '').toLowerCase()
    },
    triggerEvidenceUpload () {
      if (this.uploadingEvidence) {
        return
      }
      this.$refs.evidenceFileInput.click()
    },
    readFileAsBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target.result || ''
          resolve(result.split(',').pop())
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async onEvidenceFileSelected (event) {
      const file = event.target.files && event.target.files[0]
      if (!file || this.uploadingEvidence) {
        return
      }
      this.uploadingEvidence = true
      try {
        const fileBase64 = await this.readFileAsBase64(file)
        const evidence = await this.createTicketEvidence({
          ticketId: Number(this.$route.params.id),
          fileBase64,
          fileName: file.name,
          contentType: file.type
        })
        if (evidence) {
          this.evidenceList.push(evidence)
        }
      } finally {
        this.uploadingEvidence = false
        if (this.$refs.evidenceFileInput) {
          this.$refs.evidenceFileInput.value = null
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';

.ticket-detail-header {
  background-color: #ffffff;
  // Sin redondeo arriba: la card queda pegada al borde superior de la página.
  border-radius: 0 0 $max-radius $max-radius;
  box-shadow: $shadow1;
  // Bleed a los bordes de .view-page (padding: 2.5rem 3rem) para que la card
  // quede pegada arriba y a los lados, igual que el detalle de cliente en
  // front-general — solo esta vista, no se toca .view-page (es compartido).
  margin: -2.5rem -3rem 1.5rem;
  padding: 1.75rem 3rem;
  // La sombra del sidebar fijo (.aside-resume, rgba(0,0,0,0.04)) es demasiado
  // sutil para notarse una vez que la card queda pegada a su borde — se
  // agrega una línea explícita para que el límite siga siendo visible.
  border-left: 1px solid #eceef2;
}

.ticket-detail-header__description {
  border-top: 1px solid #eceef2;
  color: #5f7081;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  white-space: pre-wrap;
}

.ticket-detail-header__identity {
  align-items: center;
  display: flex;
  gap: 1.25rem;
}

.ticket-icon {
  align-items: center;
  background-color: #e8edff;
  border-radius: 50%;
  color: $blue;
  display: flex;
  flex-shrink: 0;
  font-size: 22px;
  height: 56px;
  justify-content: center;
  width: 56px;
}

.ticket-detail-header__body {
  min-width: 0;
}

.title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.35rem;
}

.title-row .title {
  color: #9aa5b4;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.status-pill {
  align-items: center;
  color: #5f7081;
  display: inline-flex;
  font-size: 11px;
  font-weight: 500;
  gap: 5px;

  .fa-circle {
    font-size: 7px;
  }
}

.subject-name {
  color: #1a2233;
  display: block;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: break-word;
}

.back-link {
  align-items: center;
  color: #9aa5b4;
  display: inline-flex;
  font-size: 12px;
  font-weight: 500;
  gap: 0.35rem;
  margin-top: 0.4rem;
  text-decoration: none;

  &:hover {
    color: $blue;
  }

  .fa-angle-left {
    font-size: 11px;
  }
}

.ticket-detail-header__stats {
  .stats-table tbody tr {
    &:nth-child(2n-1) {
      background-color: #ffffff;
    }

    &:nth-child(2n) {
      background-color: #f8f9fa;
    }

    td {
      border: none;
      font-size: 12px;
      padding: 0.7rem 1rem;
      text-align: left;

      &.label-cell {
        color: #9aa5b4;
        font-size: 11px;
        font-weight: 500;
        width: 22%;

        &--narrow {
          width: 16%;
        }
      }

      &.value-cell {
        color: #1a2233;
        font-weight: 500;
      }
    }
  }
}

.ticket-detail-header__edit-icon {
  color: $blue;
  cursor: pointer;
  font-size: 12px;
  margin-left: 0.5rem;
}

.ticket-detail-header__case-link {
  align-items: center;
  color: $blue;
  display: inline-flex;
  gap: 0.35rem;

  &:hover {
    text-decoration: underline;
  }

  .fa-external-link-alt {
    font-size: 10px;
  }
}

.ticket-detail-header__folio {
  background-color: #f4f6fa;
  border-radius: $min-radius;
  color: #5f7081;
  display: inline-block;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  letter-spacing: 0.03em;
  padding: 2px 8px;
}

.ticket-detail-header__actions {
  margin-top: 0.85rem;
  text-align: right;
}

// Mismo tratamiento visual que .btn-danger en el front de Portal Control
// (borde y texto en color, fondo transparente, se invierte en hover), pero
// en verde: cerrar un ticket es la acción deseada, no una destructiva.
.ticket-close-btn {
  background: #fff;
  border: 1px solid #1f9d63;
  border-radius: 8px;
  color: #1f9d63;
  font-weight: 500;
  padding: 0.5rem 0.9rem;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #1f9d63;
    color: #fff;
  }
}

@media screen and (min-width: 769px) {
  .ticket-detail-header .column:first-child {
    position: relative;

    &::after {
      background-color: #eceef2;
      content: '';
      height: 100%;
      position: absolute;
      right: 0;
      top: 0;
      width: 1px;
    }
  }
}

.ticket-thread-card,
.ticket-evidence-card {
  background-color: #ffffff;
  border-radius: $max-radius;
  box-shadow: $shadow1;
}

.ticket-thread-card {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.5rem;
  padding: 1.25rem 1.5rem 1.5rem;
}

.ticket-thread-card__header {
  align-items: center;
  border-bottom: 1px solid #eceef2;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
}

.ticket-thread-card__title {
  color: #1a2233;
  font-size: 15px;
  font-weight: 600;
}

.ticket-thread-card__count {
  color: #9aa5b4;
  font-size: 12px;
}

.ticket-thread {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}

.ticket-thread__empty {
  color: #9aa5b4;
  font-size: 13px;
  margin-bottom: 0;
  padding: 1rem 0;
  text-align: center;
}

.ticket-message {
  display: flex;
  gap: 0.75rem;

  &.-is-support {
    .ticket-message__avatar {
      background-color: #36475b;
      color: #ffffff;
    }

    .ticket-message__bubble {
      background-color: #ffffff;
      border: 1px solid #e6e9ef;
      border-top-left-radius: 4px;
    }

    .ticket-message__content {
      align-items: flex-start;
    }

    .ticket-message__meta {
      justify-content: flex-start;
    }
  }

  &.-is-client {
    flex-direction: row-reverse;

    .ticket-message__avatar {
      background-color: #e8edff;
      color: #4c70ff;
    }

    .ticket-message__bubble {
      background-color: #4c70ff;
      border-top-right-radius: 4px;
    }

    .ticket-message__text {
      color: #ffffff;
    }

    .ticket-message__content {
      align-items: flex-end;
    }

    .ticket-message__meta {
      flex-direction: row-reverse;
      justify-content: flex-end;
    }
  }
}

.ticket-message__avatar {
  align-items: center;
  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.ticket-message__content {
  display: flex;
  flex-direction: column;
  max-width: 72%;
}

.ticket-message__meta {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.35rem;
}

.ticket-message__author {
  color: #1a2233;
  font-size: 12.5px;
  font-weight: 600;
}

.ticket-message__role {
  font-size: 10px;
}

.ticket-message__date {
  color: #9aa5b4;
  font-size: 11px;
}

.ticket-message__bubble {
  border-radius: 12px;
  padding: 0.7rem 1rem;
}

.ticket-message__text {
  font-size: 13.5px;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
}

.ticket-reply {
  border-top: 1px solid #eceef2;
  padding-top: 1.25rem;
}

.ticket-reply__label {
  color: #1a2233;
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.ticket-reply__textarea {
  border-color: #d0dae6;
  border-radius: $min-radius;
  font-size: 13px;
  resize: none;

  &:focus {
    border-color: #4c70ff;
    box-shadow: none;
  }
}

.ticket-reply__footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-top: 0.6rem;
}

.ticket-reply__counter {
  color: #b0b8c4;
  font-size: 11px;
}

.ticket-reply__submit {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.ticket-evidence-card {
  margin-bottom: 1.5rem;
  padding: 1.25rem 1.5rem 1.5rem;
}

.ticket-evidence-card__header {
  align-items: center;
  border-bottom: 1px solid #eceef2;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
}

.ticket-evidence-card__title {
  color: #1a2233;
  font-size: 15px;
  font-weight: 600;
}

.ticket-evidence-card__upload-btn {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.ticket-evidence-grid {
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}

.evidence-item {
  border: 1px solid #e6e9ef;
  border-radius: $min-radius;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: $shadow1;
    transform: translateY(-2px);
  }
}

.evidence-item__preview {
  align-items: center;
  background-color: #f4f6fa;
  display: flex;
  height: 96px;
  justify-content: center;
  overflow: hidden;
}

.evidence-item__image {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.evidence-item__video-thumb {
  align-items: center;
  background: linear-gradient(160deg, #0f172a 0%, #111827 45%, #0b1220 100%);
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.evidence-item__play-icon {
  color: #ffffff;
  font-size: 26px;
  opacity: 0.9;
}

.evidence-item__doc-thumb {
  align-items: center;
  color: #7c8ba1;
  display: flex;
  font-size: 26px;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.evidence-item__name {
  color: #1a2233;
  font-size: 11.5px;
  overflow: hidden;
  padding: 0.5rem 0.6rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ticket-tag {
  align-items: center;
  display: inline-flex;
  font-weight: 500;
  gap: 5px;
  min-width: 96px;
}

// Mejor contraste que el default de Bulma para los estados "light"
.tag.is-light {
  background-color: #eceef2;
  color: #5f7081;
}

.tag.is-info.is-light {
  background-color: #e3f2ff;
  color: #0074cc;
}

.tag.is-warning.is-light {
  background-color: #fff4e0;
  color: #b26a00;
}

.tag.is-danger.is-light {
  background-color: #ffe8e8;
  color: #d32f2f;
}

.tag.is-success.is-light {
  background-color: #e3f8ee;
  color: #1f9d63;
}

@media screen and (min-width: 769px) {
  .ticket-detail-evidence-column {
    position: sticky;
    top: 1.5rem;
  }
}

// Texto del modal de confirmación de "Cerrar ticket" más grande y centrado.
// No se toca modal.scss/modal-component.scss (global, usado por todos los modales);
// se sobreescribe solo dentro de este modal específico.
.ticket-close-confirm-modal {
  // El modal no usa slot (no manda contenido en el body), así que
  // .modal-card-body queda con su padding vacío por defecto (~44px)
  // dejando el texto "flotando" arriba y un hueco muerto antes del footer.
  // Lo colapsamos para que título+texto+botones se vean como un bloque
  // compacto y centrado, no el texto pegado arriba con espacio de sobra.
  :deep(.modal-card-body) {
    padding: 0;
    min-height: 0;
  }

  :deep(.text-description) {
    font-size: 1.05rem !important;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 3.5rem;
    padding: 0.5rem 0;
  }

  // Botón de confirmar más "guapo": pill sólido en vez del botón Bulma plano.
  :deep(.actions .button.is-danger) {
    border: none;
    border-radius: 10px;
    padding: 0.8rem 1.5rem;
    font-weight: 600;
    min-width: 160px;
  }
}
</style>
